import { NgClass, NgIf } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PhoneMaskDirective } from '@curbnturf/cnt-masks/src/lib/phone/phone-mask.directive';
import { DisplayValidationErrorsDirective, ILandOwnerForm } from '@curbnturf/form-helpers';

@Component({
  selector: 'curbnturf-land-owner',
  templateUrl: './land-owner.component.html',
  styleUrls: ['./land-owner.component.scss'],
  standalone: true,
  imports: [NgIf, FormsModule, ReactiveFormsModule, NgClass, DisplayValidationErrorsDirective, PhoneMaskDirective],
})
export class LandOwnerComponent implements OnChanges {
  @Input() landOwner: FormGroup<ILandOwnerForm>;
  @Input() boondocking = false;
  @Input() editView = false;

  landOwnerError: string | undefined;
  privateHostingError: string | undefined;
  representError: string | undefined;
  landOwnerNameError: string | undefined;
  privateOwnerDetailsError: string | undefined;

  get address() {
    if (this.landOwner) {
      return this.landOwner.get('address');
    }

    return;
  }

  get selfHosting() {
    if (this.landOwner) {
      const self = this.landOwner.get('self');
      if (self) {
        return self.value === null ? undefined : self.value;
      }
    }

    return;
  }

  set selfHosting(value: boolean | undefined) {
    if (value !== this.selfHosting && this.landOwner) {
      const self = this.landOwner.get('self') as FormControl<boolean | null>;
      if (self) {
        self.setValue(value === undefined ? null : value);
        this.landOwnerError = undefined;
      }

      this.clearContact();
      this.setValidationRules(this.privateHosting);

      this.landOwner.markAsDirty();
    }
  }

  get privateHosting() {
    if (this.landOwner) {
      const privateInput = this.landOwner.get('private');
      if (privateInput) {
        return privateInput.value === null ? undefined : privateInput.value;
      }
    }

    return;
  }

  set privateHosting(value: boolean | undefined) {
    if (value !== this.privateHosting && this.landOwner) {
      const privateInput = this.landOwner.get('private') as FormControl<boolean | null>;
      if (privateInput) {
        this.landOwnerNameError = undefined;
        privateInput.setValue(value === undefined ? null : value);
      }

      this.clearContact();
      this.setValidationRules(value);

      this.landOwner.markAsDirty();
    }
  }

  get represent() {
    if (this.landOwner) {
      const represent = this.landOwner.get('represent');
      if (represent) {
        return represent.value === null ? undefined : represent.value;
      }
    }

    return;
  }

  set represent(value: boolean | undefined) {
    if (this.landOwner) {
      const represent = this.landOwner.get('represent') as FormControl<boolean | null>;
      if (value !== this.represent && represent) {
        represent.setValue(value === undefined ? null : value);
        this.landOwner.markAsDirty();
      }
    }
  }

  get lastName() {
    if (this.landOwner) {
      return this.landOwner.get('lastName');
    }

    return;
  }

  ngOnChanges() {
    if (!this.selfHosting && this.landOwner) {
      const privateInput = this.landOwner.get('private') as FormControl<boolean | null>;
      if (privateInput) {
        this.setValidationRules(privateInput.value === null ? undefined : privateInput.value);
      }
    }
  }

  validate() {
    if (this.selfHosting === undefined || this.selfHosting === null) {
      this.landOwnerError = 'Are you the land owner?';
    } else {
      this.landOwnerError = undefined;
    }
    if (this.selfHosting === false) {
      if (this.boondocking) {
        if (!this.lastName?.value?.length) {
          this.landOwnerNameError = 'Please let us know who the land owner is.';
        } else {
          this.landOwnerNameError = undefined;
        }
      } else {
        if (this.privateHosting === undefined || this.privateHosting === null) {
          this.privateHostingError = 'Is this private land or public land?';
        } else {
          this.privateHostingError = undefined;
        }
        if (!this.lastName?.value?.length || (this.lastName?.value?.length < 2 && this.privateHosting === false)) {
          this.landOwnerNameError = 'Please let us know who the land owner is.';
        } else {
          this.landOwnerNameError = undefined;
        }
        if (
          this.privateHosting &&
          (!this.lastName?.value?.length ||
            this.lastName?.value?.length < 2 ||
            // @ts-ignore - it's fine, this is fine.
            this.landOwner?.get('firstName')?.value?.length < 2 ||
            // @ts-ignore - it's fine, this is fine.
            this.landOwner?.get('phone')?.value?.length < 10)
        ) {
          this.privateOwnerDetailsError = 'Please complete the details for the land owner';
        } else {
          this.privateOwnerDetailsError = undefined;
        }
        if (this.represent === undefined || this.represent === null) {
          this.representError = 'Do your represent the landowner?';
        } else if (this.represent === false) {
          this.representError = 'You must be a representative of the owner to rent the land.';
        } else {
          this.representError = undefined;
        }
      }
    }
  }

  private clearContact() {
    if (this.landOwner && this.address) {
      const firstNameInput = this.landOwner.get('firstName');
      if (firstNameInput) {
        firstNameInput.setValue('');
      }
      const lastNameInput = this.landOwner.get('lastName');
      if (lastNameInput) {
        lastNameInput.setValue('');
      }
      const phoneInput = this.landOwner.get('phone');
      if (phoneInput) {
        phoneInput.setValue('');
      }
      const addressAddressInput = this.address.get('address');
      if (addressAddressInput) {
        addressAddressInput.setValue('');
      }
      const addressCityInput = this.address.get('city');
      if (addressCityInput) {
        addressCityInput.setValue('');
      }
      const addressStateInput = this.address.get('state');
      if (addressStateInput) {
        addressStateInput.setValue('');
      }
      const addressZipcodeInput = this.address.get('zipcode');
      if (addressZipcodeInput) {
        addressZipcodeInput.setValue('');
      }
      const representInput = this.landOwner.get('represent');
      if (representInput) {
        representInput.setValue(null);
      }
    }
  }

  private setValidationRules(privateValue?: boolean) {
    const selfHosting = this.selfHosting;

    const lastNameInput = this.landOwner.get('lastName');
    if (selfHosting && lastNameInput) {
      lastNameInput.clearValidators();
      lastNameInput.updateValueAndValidity();
    } else if (selfHosting === false && lastNameInput) {
      lastNameInput.setValidators(Validators.required);
    }

    if (selfHosting !== false || this.boondocking) {
      const representInput = this.landOwner.get('represent');
      if (representInput) {
        representInput.clearValidators();
        representInput.updateValueAndValidity();
      }
    } else {
      const representInput = this.landOwner.get('represent');
      if (representInput) {
        representInput.setValidators(Validators.requiredTrue);
      }
    }

    if (this.landOwner && this.address) {
      if (privateValue && !selfHosting) {
        const firstNameInput = this.landOwner.get('firstName');
        if (firstNameInput) {
          firstNameInput.setValidators(Validators.required);
        }
        const phoneInput = this.landOwner.get('phone');
        if (phoneInput) {
          phoneInput.setValidators(Validators.required);
        }
        const addressAddressInput = this.address.get('address');
        if (addressAddressInput) {
          addressAddressInput.setValidators(Validators.required);
        }
        const addressCityInput = this.address.get('city');
        if (addressCityInput) {
          addressCityInput.setValidators(Validators.required);
        }
        const addressStateInput = this.address.get('state');
        if (addressStateInput) {
          addressStateInput.setValidators(Validators.required);
        }
        const addressZipcodeInput = this.address.get('zipcode');
        if (addressZipcodeInput) {
          addressZipcodeInput.setValidators(Validators.required);
        }
      } else {
        const firstNameInput = this.landOwner.get('firstName');
        if (firstNameInput) {
          firstNameInput.clearValidators();
          firstNameInput.updateValueAndValidity();
        }
        const phoneInput = this.landOwner.get('phone');
        if (phoneInput) {
          phoneInput.clearValidators();
          phoneInput.updateValueAndValidity();
        }
        const addressAddressInput = this.address.get('address');
        if (addressAddressInput) {
          addressAddressInput.clearValidators();
          addressAddressInput.updateValueAndValidity();
        }
        const addressCityInput = this.address.get('city');
        if (addressCityInput) {
          addressCityInput.clearValidators();
          addressCityInput.updateValueAndValidity();
        }
        const addressStateInput = this.address.get('state');
        if (addressStateInput) {
          addressStateInput.clearValidators();
          addressStateInput.updateValueAndValidity();
        }
        const addressZipcodeInput = this.address.get('zipcode');
        if (addressZipcodeInput) {
          addressZipcodeInput.clearValidators();
          addressZipcodeInput.updateValueAndValidity();
        }
      }
    }
  }
}
